import { Canvas } from '@react-three/fiber';
import { Children, FC, ReactNode, useMemo, createRef, Suspense, lazy } from 'react';
import { useAppContext } from '@/context/AppContext';
import { EffectComposer } from '@react-three/postprocessing';
import { Effect } from '@/types/core/Effect';
import { Mesh } from '@/types/core/Mesh';
import { Light } from '@/types/core/Light';
import { Camera } from '@/types/core/Camera';
import { Item } from '@/types/core/Item';
import { World } from '@/types/core/World';
import { v4 as uuidv4 } from 'uuid';
import React from 'react';
import { Vector3, Object3D } from 'three';

interface PreviewSceneProps {
  includeDefaultItems?: boolean;
  isHovered?: boolean;
  item?: Item | null;
  type?: string;
}

// Simple placeholder component for non-hovered state
const SimplePlaceholder: FC<{ type?: string }> = ({ type }) => {
  // Different placeholder based on item type
  const color = useMemo(() => {
    if (!type) return '#cccccc';
    if (type.startsWith('mesh')) return '#4080ff';
    if (type.startsWith('light')) return '#ffcc00';
    if (type.startsWith('camera')) return '#00cc88';
    if (type.startsWith('effect')) return '#ff6688';
    return '#cccccc';
  }, [type]);

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      backgroundColor: color,
      opacity: 0.3,
      borderRadius: '4px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div style={{ 
        width: '50%', 
        height: '50%', 
        backgroundColor: color,
        borderRadius: '50%'
      }} />
    </div>
  );
};

const PreviewScene: FC<PreviewSceneProps> = ({ includeDefaultItems = false, isHovered = false, item, type }) => {
  const { createItem } = useAppContext();

  // Only create the preview world when hovered to save resources
  const previewWorld = useMemo(() => {
    // If not hovered, don't create the world
    if (!isHovered) return null;

    const worldId = uuidv4();
    const world: World = {
      id: worldId,
      name: `Preview_${worldId}`,
      items: {} as Record<string, Item>
    };

    // Add preview item if provided
    if (item) {
      const previewItemId = `${worldId}_item`;
      world.items[previewItemId] = {
        ...item,
        id: previewItemId,
        previewRole: 'main' as const
      };
    }

    // Add default camera
    const camera = createItem('camera', `Preview_${worldId}_Camera`);
    if (camera) {
      const cameraId = `${worldId}_camera`;
      world.items[cameraId] = { 
        ...camera, 
        id: cameraId,
        previewRole: 'helper' as const,
        properties: {
          ...camera.properties,
          position: new Vector3(0, 2, 5),
          lookAt: new Vector3(0, 0, 0)
        }
      };
    }

    // Add default light
    const light = createItem('light.directional', `Preview_${worldId}_Light`);
    if (light) {
      const lightId = `${worldId}_light`;
      world.items[lightId] = { 
        ...light, 
        id: lightId,
        previewRole: 'helper' as const,
        properties: {
          ...light.properties,
          intensity: 1,
          color: '#ffffff'
        }
      };
    }

    return world;
  }, [item, createItem, isHovered]);

  // Render items using the same logic as World.tsx
  const renderItem = (item: Item) => {
    if (!item.render) return null;
    // Only pass isPreview=true for the main preview item
    const isMainPreview = item.previewRole === 'main';
    // Create a ref for the item
    const itemRef = createRef<Object3D | null>();
    const element = item.render(item.properties, isMainPreview, itemRef);
    if (!element || !React.isValidElement(element)) return null;
    return React.cloneElement(element, { key: item.id });
  };

  // If not hovered, render a simple placeholder instead of the 3D scene
  if (!isHovered || !previewWorld) {
    return <SimplePlaceholder type={type} />;
  }

  // Check if the main item is an effect
  const isEffectItem = item?.type === 'effect' || type?.startsWith('effect');

  // For effect items, we'll render a simple scene to show it's an effect
  if (isEffectItem) {
    return (
      <Canvas shadows gl={{ antialias: false, powerPreference: 'low-power' }}>
        <Suspense fallback={null}>
          {/* Render camera */}
          {Object.values(previewWorld.items)
            .filter((item) => item.type === 'camera')
            .map(renderItem)}
            
          {/* Render a simple sphere to represent the effect */}
          <mesh>
            <sphereGeometry args={[1, 16, 16]} />
            <meshStandardMaterial color="#4080ff" />
          </mesh>
          
          {/* Add a simple light */}
          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} intensity={1} />
        </Suspense>
      </Canvas>
    );
  }

  return (
    <Canvas shadows gl={{ antialias: false, powerPreference: 'low-power' }}>
      <Suspense fallback={null}>
        {/* Render camera */}
        {Object.values(previewWorld.items)
          .filter((item) => item.type === 'camera')
          .map(renderItem)}

        {/* We don't render effects in preview mode as they cause issues */}
        {/* Render world objects */}
        <group>
          {/* Render meshes */}
          {Object.values(previewWorld.items)
            .filter((item) => item.type === 'mesh')
            .map(renderItem)}

          {/* Render lights */}
          {Object.values(previewWorld.items)
            .filter((item) => item.type === 'light')
            .map(renderItem)}
        </group>
      </Suspense>
    </Canvas>
  );
};

// Use React.memo to prevent unnecessary re-renders
export default React.memo(PreviewScene); 